import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
  useTheme,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Select,
  Option
} from '@mui/joy';
import { useMediaQuery } from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../assets/images/geek-green-g-logo.png';
import { signOut } from '../../../services/authService';
import { HEADER_HEIGHT } from '.';
import { getCurrentUser } from '../../../apiconfig/api';
import { CurrentUser, Employer } from '../../../types';
import { useSession } from '../../../context/SessionContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const { sessionEmployerId, setSessionEmployerId } = useSession();
  
  // Check if user is logged in by looking for session_token
  const isLoggedIn = !!localStorage.getItem('session_token');
  
  const handleAvatarClick = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSignOut = () => {
    signOut();
    handleDrawerClose();
    navigate('/');
    window.location.reload();
  };

  // Fetch current user data when logged in
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!isLoggedIn) {
        setCurrentUser(null);
        return;
      }

      try {
        setLoading(true);
        const response = await getCurrentUser();
        setCurrentUser(response.data.user);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, [isLoggedIn]);

  const handleSignIn = () => {
    navigate(`/auth/signin?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  };

  const handleAdminDashboard = () => {
    navigate('/admin/dashboard');
    handleDrawerClose();
  };

  const handleEmployerSettings = () => {
    navigate('/hiring/settings');
    handleDrawerClose();
  };

  const handleSwitchCompany = () => {
    setSelectedCompanyId(sessionEmployerId);
    setCompanyModalOpen(true);
    handleDrawerClose();
  };

  const handleCompanySelect = (employerId: string | null) => {
    if (employerId && employerId !== sessionEmployerId) {
      setSessionEmployerId(employerId);
      setCompanyModalOpen(false);
      setSelectedCompanyId(null);
      navigate('/hiring/positions');
    }
  };

  const handleModalClose = () => {
    setCompanyModalOpen(false);
    setSelectedCompanyId(null);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (companyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [companyModalOpen]);

  // Get current employer from user's employers array
  const getCurrentEmployer = (): Employer | null => {
    if (!currentUser?.employers || !sessionEmployerId) return null;
    return currentUser.employers.find(emp => emp.uuid === sessionEmployerId) || null;
  };

  const currentEmployer = getCurrentEmployer();

  // Helper function to get user initials
  const getUserInitials = (fullName: string | null | undefined) => {
    if (!fullName || typeof fullName !== 'string') {
      return '';
    }
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navLinks = [
    { label: 'Posts', href: '/hiring/positions' },
    { label: 'People', href: '/hiring/candidates' },
    { label: 'Meetings', href: '/hiring/meetings' }
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: HEADER_HEIGHT,
        bgcolor: 'white',
        borderBottom: '1px solid #e0e0e0',
        px: { xs: 2, md: 4 },
        py: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Logo */}
          <Box
            component="a"
            href="/"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img src={Logo} alt="Geek Logo" style={{ height: '32px' }} />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && isLoggedIn && (
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'center',
              }}
            >
              {navLinks.map((link) => (
                <Typography
                  key={link.label}
                  level="body-md"
                  sx={{
                    cursor: 'pointer',
                    color: '#1A1B1D',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#2e2d7e',
                    },
                  }}
                  onClick={() => navigate(link.href)}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          )}

          {/* Desktop Auth Section */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isLoggedIn ? (
                <Avatar
                  onClick={handleAvatarClick}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: currentUser ? '#2e2d7e' : 'white',
                    border: '1px solid #e0e0e0',
                    color: currentUser ? 'white' : '#2e2d7e',
                    borderRadius: '50%',
                    '&:hover': {
                      borderColor: '#2e2d7e',
                      bgcolor: currentUser ? '#1e1d6e' : '#f8f9fa',
                    },
                  }}
                >
                  {currentUser && getUserInitials(currentUser.fullName) ? getUserInitials(currentUser.fullName) : <PersonIcon />}
                </Avatar>
              ) : (
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={handleSignIn}
                  sx={{
                    color: '#2e2d7e',
                    borderColor: '#2e2d7e',
                    borderRadius: '6px',
                    height: '36px',
                    px: 2,
                    '&:hover': {
                      borderColor: '#1e1d6e',
                      bgcolor: 'rgba(46, 45, 126, 0.04)',
                    },
                  }}
                >
                  Sign in
                </Button>
              )}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && isLoggedIn && (
            <IconButton
              onClick={handleAvatarClick}
              sx={{
                color: '#1A1B1D',
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile Sign In Button */}
          {isMobile && !isLoggedIn && (
            <Button
              variant="outlined"
              size="sm"
              onClick={handleSignIn}
              sx={{
                color: '#2e2d7e',
                borderColor: '#2e2d7e',
                height: '36px',
                px: 2,
                '&:hover': {
                  borderColor: '#1e1d6e',
                  bgcolor: 'rgba(46, 45, 126, 0.04)',
                },
              }}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Container>

      {/* Drawer for both mobile and desktop */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            p: 2,
          },
        }}
      >
        <Box sx={{ mt: 2 }}>
          {/* Navigation Links - Only on Mobile */}
          {isMobile && (
            <>
              <List>
                {navLinks.map((link) => (
                  <ListItem key={link.label}>
                    <ListItemButton
                      onClick={() => {
                        navigate(link.href);
                        handleDrawerClose();
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        level="body-md"
                        sx={{
                          fontWeight: 500,
                          color: '#1A1B1D',
                        }}
                      >
                        {link.label}
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* User Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: '#f8f9fa',
            }}
          >
            <Avatar
              sx={{
                bgcolor: currentUser ? '#2e2d7e' : 'white',
                border: '1px solid #e0e0e0',
                color: currentUser ? 'white' : '#2e2d7e',
                borderRadius: '50%',
              }}
                          >
                {currentUser && getUserInitials(currentUser.fullName) ? getUserInitials(currentUser.fullName) : <PersonIcon />}
              </Avatar>
            <Box>
              <Typography level="body-md" sx={{ fontWeight: 500 }}>
                {currentUser ? currentUser.fullName : 'User'}
              </Typography>
              {currentUser && (
                <Typography level="body-sm" sx={{ color: '#7B7F85' }}>
                  {currentUser.email}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Company Section */}
          {currentEmployer && (
            <>
              <Typography
                level="body-sm"
                sx={{
                  color: '#7B7F85',
                  fontWeight: 600,
                  px: 2,
                  mb: 1,
                }}
              >
                {currentEmployer.companyName.toUpperCase()}
              </Typography>
              <List>
                <ListItem>
                  <ListItemButton
                    onClick={handleEmployerSettings}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <SettingsIcon sx={{ mr: 2, fontSize: 20 }} />
                    <Typography
                      level="body-md"
                      sx={{
                        fontWeight: 500,
                        color: '#1A1B1D',
                      }}
                    >
                      Employer Settings
                    </Typography>
                  </ListItemButton>
                </ListItem>
                {currentUser?.employers && currentUser.employers.length > 1 && (
                  <ListItem>
                    <ListItemButton
                      onClick={handleSwitchCompany}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <BusinessIcon sx={{ mr: 2, fontSize: 20 }} />
                      <Typography
                        level="body-md"
                        sx={{
                          fontWeight: 500,
                          color: '#1A1B1D',
                        }}
                      >
                        Switch Company
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Admin Section */}
          {currentUser?.isSystemAdmin && (
            <>
              <Typography
                level="body-sm"
                sx={{
                  color: '#7B7F85',
                  fontWeight: 600,
                  px: 2,
                  mb: 1,
                }}
              >
                ADMIN
              </Typography>
              <List>
                <ListItem>
                  <ListItemButton
                    onClick={handleAdminDashboard}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <DashboardIcon sx={{ mr: 2, fontSize: 20 }} />
                    <Typography
                      level="body-md"
                      sx={{
                        fontWeight: 500,
                        color: '#1A1B1D',
                      }}
                    >
                      Admin Dashboard
                    </Typography>
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Sign Out */}
          <List>
            <ListItem>
              <ListItemButton
                onClick={handleSignOut}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  color: 'danger.500',
                  '&:hover': {
                    bgcolor: 'danger.50',
                  },
                }}
              >
                <LogoutIcon sx={{ mr: 2, fontSize: 20, color: 'danger.500' }} />
                <Typography
                  level="body-md"
                  sx={{
                    fontWeight: 500,
                    color: 'danger.500',
                  }}
                >
                  Sign out
                </Typography>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Company Switching Modal */}
      <Modal open={companyModalOpen} onClose={handleModalClose}>
        <ModalDialog 
          size="sm" 
          sx={{ 
            maxWidth: 400,
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Switch Company
          </Typography>
          <Typography level="body-md" sx={{ mb: 3, color: 'neutral.600' }}>
            Select the company you want to work with:
          </Typography>
          <Stack spacing={3}>
            <Select
              value={selectedCompanyId || ''}
              onChange={(event, newValue) => setSelectedCompanyId(newValue)}
              placeholder="Choose a company..."
              sx={{ width: '100%' }}
            >
              {currentUser?.employers?.sort((a, b) => a.companyName.localeCompare(b.companyName)).map((employer) => (
                <Option key={employer.uuid} value={employer.uuid}>
                  <Box>
                    <Typography level="body-md" sx={{ fontWeight: 500 }}>
                      {employer.companyName}
                    </Typography>
                    {employer.address && (
                      <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
                        {employer.address}
                      </Typography>
                    )}
                  </Box>
                </Option>
              ))}
            </Select>
            
            <Button
              variant="solid"
              color="primary"
              fullWidth
              disabled={!selectedCompanyId || selectedCompanyId === sessionEmployerId}
              onClick={() => handleCompanySelect(selectedCompanyId)}
            >
              Switch to {currentUser?.employers?.find(emp => emp.uuid === selectedCompanyId)?.companyName || 'Company'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default Header; 